import { CustomerProfile, MonoMenuEntree, MonoMenuEntreeGroup } from '@/models';
import { ui } from '@/constants';
import Vue from 'vue';
import { Component } from 'vue-property-decorator';
import { Routes } from '@/router';
import debounce from 'lodash/debounce';
import { awaitCondition } from '@/helpers/async';
import { capitalizeEveryWord } from '@/helpers';
import { NutritionCalculatorPageLoadEvent, NutritionLinkClickEvent } from '@/services/analytics/events';
import { NutritionLinkClickType } from '@/services/analytics/platforms/adobe-analytics/models';
import CmgNutritionCalculatorMealBuilderMobileGraphFooter from '../CmgNutritionCalculatorMealBuilderMobileGraphFooter/cmg-nutrition-calculator-meal-builder-mobile-graph-footer';

@Component({
  name: 'CmgNutritionCalculatorMealBuilder',
})
export default class CmgNutritionCalculatorMealBuilder extends Vue {
  showDietaryFilterHint = false;
  hoverNutritionPreferences = false;

  get groups(): MonoMenuEntreeGroup[] {
    if (this.$appStore.state.mealBuilderV2.topLevelMenuItemTitle === 'Lifestyle Bowl') {
      return [
        {
          type: 'pce',
          title: 'LIFESTYLE BOWL',
          subtitle: '',
          contentGroupName: 'PCEGroup',
          items: [],
        },
      ];
    }
    return this.entree()?.groups ?? [];
  }

  get hasLinkedEntree(): boolean {
    const entree = this.entree();
    return !!entree?.linkedEntreeId && !!entree?.linkedEntreeTypeOptions;
  }

  entree(): MonoMenuEntree | undefined {
    if (this.$appStore.state.mealBuilderV2.entreeId) {
      return this.$services.monoMenu.getMonoMenuEntree(this.$appStore.state.mealBuilderV2.entreeId);
    }

    return undefined;
  }

  get linkedEntreeTitle() {
    return this.entree()?.linkedEntreeTypeOptions?.title;
  }

  get customer(): CustomerProfile | null {
    return this.$appStore.state.customer.profile;
  }

  get hasDietaryTagsSet(): boolean {
    return this.customer?.dietaryTags != null && this.customer.dietaryTags.length > 0;
  }

  get dietsAndAllergensSymbol(): string {
    return this.hoverNutritionPreferences ? this.$appStore.state.cms.images.icons.nutritionFilterHover.default : this.$appStore.state.cms.images.icons.nutritionFilter.default;
  }

  get isDesktop(): boolean {
    return this.$appStore.state.ui.clientWidth >= ui.breakpoints.desktop;
  }

  private dataIsReady() {
    return !!this.$services.monoMenu.getTopLevelMenuByTitle('Burrito');
  }

  async mounted() {
    const topLevelName = this.$router.currentRoute.params['topLevelName'];

    await awaitCondition(() => !!this.$appStore.state.ui.clientWidth);
    await this.$services.monoMenu.awaitCacheItemsAndBuildMenu();

    // dont overwrite the state if a meal is already loaded
    if (!this.$appStore.state.mealBuilderV2.entreeId) {
      const result = this.$services.mealBuilderV2.openEmptyTopLevelMenuItem(this.getTopLevelTitle(topLevelName));

      result.catch((error: any) => {
        this.$router.push({ name: Routes.PageNotFound });
      });
    }

    if (!this.$appStore.state.ui.dietaryFilterHintShown && !this.customer?.dietaryTags) {
      this.showDietaryFilterHint = true;
      this.$services.ui.setDietaryFilterHintShown(true);
      setTimeout(() => {
        this.showDietaryFilterHint = false;
      }, 4000);
    }

    if (!this.$appStore.state.monoMenu.resizeListener) {
      const resizeListener = debounce(() => {
        this.$services.monoMenu.setMetadataCache();
      }, 500);
      this.$services.monoMenu.setMonoMenuResizeListener(resizeListener);

      window.addEventListener('resize', resizeListener);
    }

    this.$services.analytics.send(new NutritionCalculatorPageLoadEvent('meal-builder', topLevelName));
  }

  isOptionSelected(optionTitle: string) {
    return optionTitle === this.entree()?.entreeOptionTitle;
  }

  selectLinkedEntree(option: any) {
    if (option.optionTitle !== this.entree()?.entreeOptionTitle) {
      this.$services.mealBuilderV2.switchToLinkedEntreeType();
    }
  }

  onSelectTags(event: any) {
    this.$services.ui.pushEventTargetAsLastSelectedElement(event);
    this.$services.ui.setModalVisibility('dietaryFiltersModal', true);
    this.$services.ui.firstFocus();
    
    this.$services.analytics.send(new NutritionLinkClickEvent(NutritionLinkClickType.NutritionPreferences));
  }

  getTopLevelTitle(topLevelName: string) {
    return capitalizeEveryWord(topLevelName.replace(/[-]/g, ' '));
  }

  notifyMobileFooter() {
    setTimeout(() => (this.$refs.mobileFooter as CmgNutritionCalculatorMealBuilderMobileGraphFooter).onScroll(), 10);
  }
}
